// "As the CSR saw it" (2026-10-29, docs/feedback.md): a Send Feedback
// report's screen state, drawn by the real suggestion panel in its read-only
// mode (SuggestionPanel's screenView prop): the same step cards, the same
// field controls, disabled, and the same reply box, read-only, fed from the
// stored screen state instead of live data. No button in it calls anything.
//
// Around it: the report's facts (order, company, channel, client, intent,
// flow), the version warning when the contact has a newer draft, a "Changed
// only" toggle, the template's name over the AI draft and the box side by
// side with the changes marked, and under each step what the CSR changed from
// the AI's prefilled value and what the step submitted (when it was already
// processed). Admin-only: the screen state is read through the admin-only
// GET /api/admin/feedback/:id/screen-state.

import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Chip, CircularProgress, FormControlLabel, Link, Stack, Switch, Typography } from '@mui/material'
import DraggableModal from './DraggableModal.jsx'
import SuggestionPanel from './SuggestionPanel.jsx'
import { fetchFeedbackScreenState } from './api.js'
import { convNumberPath } from './contactPath.js'
import { stepChanges, changeLine, changedStepIndexes, versionWarning, replyWordDiff, submittedSummary } from './feedbackScreenState.js'

function modalSize() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  return { width: Math.max(Math.min(Math.round(vw * 0.9), 1500), Math.min(640, vw)), height: Math.max(Math.round(vh * 0.88), Math.min(400, vh)) }
}

function Facts({ facts }) {
  const parts = [
    facts.orderNumber ? `Order ${facts.orderNumber}` : null,
    facts.company, facts.channel, facts.client,
    facts.intent ? `intent: ${facts.intent}` : null,
    facts.flowKey ? `flow: ${facts.flowKey}` : null,
  ].filter(Boolean)
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid="screen-facts">
      {facts.convNumber != null && <Typography sx={{ fontWeight: 700 }}>#{facts.convNumber}</Typography>}
      {parts.map((p) => <Chip key={p} size="small" variant="outlined" label={p} />)}
    </Stack>
  )
}

function DiffText({ parts, tone, testId }) {
  return (
    <Box data-testid={testId} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 14, lineHeight: 1.5, p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', minHeight: 60 }}>
      {parts.length ? parts.map((p, i) => (p.changed
        ? <Box key={i} component="mark" data-testid="screen-reply-change" sx={{ bgcolor: tone === 'removed' ? 'error.light' : 'success.light', color: 'text.primary', textDecoration: tone === 'removed' ? 'line-through' : 'none', borderRadius: 0.5, px: 0.25 }}>{p.text}</Box>
        : <span key={i}>{p.text}</span>)) : <Typography variant="body2" color="text.secondary">(empty)</Typography>}
    </Box>
  )
}

function ReplyCompare({ state }) {
  const reply = state.reply
  const diff = useMemo(() => replyWordDiff(reply?.draft ?? '', reply?.box ?? ''), [reply?.draft, reply?.box])
  if (!reply?.hasBox && reply?.box == null) return null
  const template = state.template
  return (
    <Box data-testid="screen-reply-compare">
      {template?.title && (
        <Typography variant="body2" sx={{ mb: 0.75 }} data-testid="screen-template">
          Template: <b>{template.folder ? `${template.folder} / ` : ''}{template.title}</b>
          {template.source === 'agent_inserted' ? ' (inserted by the CSR)' : ' (AI selected)'}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>AI draft</Typography>
          <DiffText parts={diff.draft} tone="removed" testId="screen-reply-draft" />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Reply box, as on screen{diff.changed ? '' : ' (unchanged)'}</Typography>
          <DiffText parts={diff.box} tone="added" testId="screen-reply-box" />
        </Box>
      </Box>
    </Box>
  )
}

// What the CSR changed on one step, and what it submitted when it had run.
export function StepMarks({ step, execution }) {
  if (!step) return null
  const { fields, flags } = stepChanges(step)
  const summary = execution?.submittedParams ? submittedSummary(execution.submittedParams) : null
  if (!fields.length && !flags.length && !summary) return null
  return (
    <Stack spacing={0.25} data-testid="screen-step-marks" sx={{ mt: 0.25, mb: 0.75, ml: 1, pl: 1, borderLeft: 3, borderColor: fields.length || flags.length ? 'warning.main' : 'divider' }}>
      {fields.map((c) => (
        <Typography key={c.path} variant="caption" data-testid="screen-field-change" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-word' }}>
          {changeLine(c)}
        </Typography>
      ))}
      {flags.map((f) => <Typography key={f} variant="caption" color="warning.main" sx={{ display: 'block' }}>{f}</Typography>)}
      {summary && (
        <Typography variant="caption" color="text.secondary" data-testid="screen-step-submitted" sx={{ display: 'block' }}>
          Submitted{execution.success ? '' : ' (failed)'}: {summary}
        </Typography>
      )}
    </Stack>
  )
}

export default function FeedbackScreenView({ feedbackId, open = true, onClose }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [changedOnly, setChangedOnly] = useState(false)
  const [size] = useState(modalSize)

  useEffect(() => {
    if (!open || !feedbackId) return undefined
    let active = true
    setData(null); setError(null)
    fetchFeedbackScreenState(feedbackId)
      .then((d) => { if (active) setData(d) })
      .catch((e) => { if (active) setError(e.message) })
    return () => { active = false }
  }, [feedbackId, open])

  const state = data?.screenState ?? null
  const changedSet = useMemo(() => changedStepIndexes(state), [state])
  const warning = versionWarning(data?.version)
  const facts = data?.facts ?? {}
  const ctx = state?.context ?? {}
  const screenView = useMemo(() => (state && data?.suggestion ? {
    state,
    suggestion: data.suggestion,
    changedOnly: changedOnly ? changedSet : null,
    renderStepMarks: (i) => {
      const step = (state.steps ?? []).find((s) => s.index === i)
      const execution = step?.execution?.id ? (data.executions ?? []).find((e) => e.id === step.execution.id) : null
      return <StepMarks step={step} execution={execution} />
    },
  } : null), [state, data, changedOnly, changedSet])

  return (
    <DraggableModal open={open} onClose={onClose} title="As the CSR saw it" testId="screen-view-modal"
      width={size.width} height={size.height} minWidth={480} minHeight={300} centered
      headerActions={state ? (
        <FormControlLabel sx={{ mr: 1 }} control={<Switch size="small" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} inputProps={{ 'data-testid': 'screen-changed-only' }} />}
          label={<Typography variant="caption">Changed only ({changedSet.size})</Typography>} />
      ) : null}>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
      {data && (
        <Stack spacing={1.5} data-testid="screen-view">
          <Facts facts={facts} />
          {warning && (
            <Alert severity="warning" data-testid="screen-version-warning">
              {warning}{' '}
              {facts.convNumber != null && <Link href={convNumberPath(facts.convNumber)} target="_blank" rel="noopener" data-testid="screen-open-contact">Open the contact</Link>}
            </Alert>
          )}
          {state?.truncated && <Alert severity="info">The panel's full state was too large to keep; the steps and the reply are shown from the summary.</Alert>}
          <ReplyCompare state={state} />
          {screenView ? (
            <Box component="fieldset" disabled data-testid="screen-panel" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, m: 0, p: 0, minWidth: 0 }}>
              <SuggestionPanel
                conversationId={facts.conversationId}
                screenView={screenView}
                fromEbay={Boolean(ctx.fromEbay)}
                channel={ctx.channel ?? facts.channel ?? null}
                client={ctx.client ?? facts.client ?? null}
                conversationStatus={ctx.conversationStatus ?? 'open'}
                templateAccount={ctx.templateAccount ?? null}
                templateTrqClientCode={ctx.templateTrqClientCode ?? null}
                templateTeam={ctx.templateTeam ?? null}
                isInternalEmail={Boolean(ctx.isInternalEmail)}
                isAdmin={Boolean(ctx.isAdmin)}
                mailbox={ctx.mailbox ?? null}
                outboundFrom={ctx.outboundFrom ?? null}
                subject={ctx.subject ?? null}
              />
            </Box>
          ) : (
            <Alert severity="info">The suggestion this report was on no longer exists; only the reply is shown.</Alert>
          )}
        </Stack>
      )}
    </DraggableModal>
  )
}
